import {ErrorHandler, LOCALE_ID, NgModule, inject, provideAppInitializer} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {BsDatepickerModule} from 'ngx-bootstrap/datepicker';
import {AppComponent} from './app.component';
import {BlockCopyPasteDirective} from './directives/block-copy-paste.directive';
import {AppInitService} from './shared/services/init.service';
import {BrowserAnimationsModule} from "@angular/platform-browser/animations";
import {SharedModule} from './shared/modules/shared.module';
import {NuMonacoEditorModule} from '@ng-util/monaco-editor';
import {HelpRedirectComponent} from "./layouts/public/help-redirect/help-redirect.component";
import {LogoutAllConfirmComponent} from "./shared/components/logout-all-confirm/logout-all-confirm.component";
import { ErrorComponent } from "./layouts/error/error.component";
import { QRScannerComponent } from "./layouts/qr-scanner/qr-scanner.component"; 
import {PermissionDirective} from "./directives/permission.directive";
import {DatePipe} from "@angular/common";
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from "@angular/common/http";
import {JwtInterceptor} from "./interceptors/jwt.Interceptor";
import {RaygunErrorHandler} from "./helpers/app.raygun.setup";
import {provideRouter, RouteReuseStrategy, withDebugTracing, withRouterConfig} from "@angular/router";
import {AuthorizationCheckService} from "./shared/services/authorization-check.service";
import {REDIRECT_ROUTES} from "./app.routes";
import { SpinnerComponent } from "./shared/components/spinner/spinner.component";

/* Import Locales */
import '@angular/common/locales/global/en-GB';
import '@angular/common/locales/global/fr';
import '@angular/common/locales/global/de';
import '@angular/common/locales/global/es';
import '@angular/common/locales/global/it';
import '@angular/common/locales/global/nl';
import '@angular/common/locales/global/es-CL';
import '@angular/common/locales/global/es-MX';
import '@angular/common/locales/global/pt';
import '@angular/common/locales/global/en-ZA';
import '@angular/common/locales/global/fr-DZ';
import '@angular/common/locales/global/en-AU';
import '@angular/common/locales/global/en-NZ';
import '@angular/common/locales/global/en-IN';
import '@angular/common/locales/global/id';
import '@angular/common/locales/global/ms';
import '@angular/common/locales/global/zh';
import '@angular/common/locales/global/ja';
import '@angular/common/locales/global/ko';
import '@angular/common/locales/global/fa';

export function initialize(initService: AppInitService) {
    return (): Promise<any> => {
        return initService.Init();
    }
}

@NgModule({
    declarations: [
        AppComponent,
        BlockCopyPasteDirective
    ],
    exports: [],
    bootstrap: [AppComponent],
    imports: [
        BrowserAnimationsModule,
        FormsModule,
        BsDatepickerModule.forRoot(),
        SharedModule.forRoot(),
        NuMonacoEditorModule.forRoot({
            baseUrl: 'lib',
            defaultOptions: {scrollBeyondLastLine: false},
            monacoLoad: () => {
                (window as any).monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
                    validate: true,
                    schemaValidation: "error",
                    enableSchemaRequest: true,
                    schemas: [
                        {
                            uri: 'https://u.fecpak.com/mi-json-schema',
                            fileMatch: ['**']
                        }
                    ]
                });
            }
        }),
        HelpRedirectComponent,
        LogoutAllConfirmComponent,
        ErrorComponent,
        PermissionDirective,
        SpinnerComponent,
        QRScannerComponent
    ], providers: [
        DatePipe,
        provideAppInitializer(() => {
            const initializerFn = (initialize)(inject(AppInitService));
            return initializerFn();
        }),
        {provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true},
        {provide: ErrorHandler, useClass: RaygunErrorHandler},
        provideRouter([
                {path: "", redirectTo: "login", pathMatch: 'full'},
                {
                    path: "error/:code",
                    loadComponent: () => import('./layouts/error/error.component').then(x => x.ErrorComponent)
                },
                {
                    path: 'help',
                    loadComponent: () => import('./layouts/public/help-redirect/help-redirect.component').then(x => x.HelpRedirectComponent)
                },
                {
                    path: 'user-guide',
                    loadChildren: () => import('./layouts/user-guide/user-guide.module').then(x => x.UserGuideModule)
                },
            
                {
                    path: '',
                    loadChildren: () => import('./layouts/public/public.module').then(x => x.PublicModule)
                },
                {
                    path: '',
                    loadChildren: () => import('./layouts/secure/secure.module').then(x => x.SecureModule),
                    canActivateChild: [AuthorizationCheckService]
                },
                {
                    path: 'mark-up-and-analysis/mark-up',
                    loadChildren: () => import('./layouts/mark-up/mark-up.module').then(x => x.MarkupModule),
                    canActivateChild: [AuthorizationCheckService]
                },
                {
                    path: 'qr-scanner',
                    loadComponent: () => import('./layouts/qr-scanner/qr-scanner.component').then(x => x.QRScannerComponent),
                },
                {
                    path: '',
                    children: REDIRECT_ROUTES,
                },
                // otherwise redirect to home
                {path: '**', redirectTo: "error/404"}
        ], withRouterConfig({
            canceledNavigationResolution: 'replace',
            paramsInheritanceStrategy: 'emptyOnly',
            urlUpdateStrategy: 'deferred'
        }),
            /*withDebugTracing()*/
        ),
        provideHttpClient(withInterceptorsFromDi()),
        {
            provide: LOCALE_ID,
            useValue: navigator.language || 'en-NZ' // Set the default locale to the user's browser language or fallback to 'en-NZ'
        } 
    ]
})
export class AppModule {
}
